import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { BarChart3, Briefcase, LogOut, Sun, Moon, Monitor, Crown, ClipboardCheck, Target, Calculator, UserPlus, Gauge } from 'lucide-react';
import LogoSynosight from './assets/synosight-logo-horizontal.svg';
import { api } from './api/client.js';
import { useAuth } from './hooks/useAuth.js';
import { useTheme } from './hooks/useTheme.js';
import { useViewRoute } from './hooks/useViewRoute.js';
import { currentPathForLogin } from './lib/routes.js';
import LoginView from './views/LoginView.jsx';
import AccountGateView from './views/AccountGateView.jsx';
import AdminMenu from './components/AdminMenu.jsx';
import AppSwitcherMenu from './components/AppSwitcherMenu.jsx';
import { ECOSYSTEM_APPS, fetchAppAccess, visibleApps } from './lib/appSwitcher.js';
import { ADMIN_GROUPS, ADMIN_VIEWS } from './views/admin/adminGroups.js';

// Vues lazy-loadées : chacune embarque Recharts et/ou de gros tableaux. Le
// chargement à la demande sort ces dépendances (dont Recharts ~250 kB) du
// bundle initial → connexion + 1er affichage plus rapides.
const ForecastView = lazy(() => import('./views/ForecastView.jsx'));
const ProspectionView = lazy(() => import('./views/ProspectionView.jsx'));
const AvantVenteView = lazy(() => import('./views/AvantVenteView.jsx'));
const ExecutiveView = lazy(() => import('./views/ExecutiveView.jsx'));
const PilotageBlView = lazy(() => import('./views/PilotageBlView.jsx'));
const SynologCheckView = lazy(() => import('./views/SynologCheckView.jsx'));
const ControleGestionView = lazy(() => import('./views/ControleGestionView.jsx'));
const OnboardingView = lazy(() => import('./views/OnboardingView.jsx'));

// Vues de la nav principale ; l'administration est un menu déroulant groupé par
// thème (synotis-support#4, comme synolog), une vue par écran (`ADMIN_VIEWS`).
const MAIN_VIEWS = [
  { key: 'forecast', label: 'Forecast', Icon: BarChart3, Component: ForecastView },
  { key: 'prospection', label: 'Prospection', Icon: Briefcase, Component: ProspectionView, managerHidden: true },
  { key: 'avant-vente', label: 'Avant-Vente', Icon: Target, Component: AvantVenteView, managerHidden: true },
  { key: 'direction', label: 'Direction', Icon: Crown, Component: ExecutiveView, execOnly: true },
  { key: 'pilotage-bl', label: 'Pilotage BL', Icon: Gauge, Component: PilotageBlView, execOrManager: true },
  { key: 'controle', label: 'Contrôle Synolog', Icon: ClipboardCheck, Component: SynologCheckView, adminOrExec: true },
  { key: 'controle-gestion', label: 'Contrôle de gestion', Icon: Calculator, Component: ControleGestionView, adminOrExec: true },
  { key: 'onboarding', label: 'Onboarding', Icon: UserPlus, Component: OnboardingView, adminOrExecOrReferent: true },
];
const VIEWS = [...MAIN_VIEWS, ...ADMIN_VIEWS];

const THEME_ICONS = { auto: Monitor, light: Sun, dark: Moon };
const THEME_LABELS = { auto: 'Thème auto', light: 'Thème clair', dark: 'Thème sombre' };

export default function App() {
  const { user, loading, authError, login, logout, isAuthenticated } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const isAdmin = user?.role === 'ADMIN' || user?.isAdmin;
  const managerScoped = Boolean(user?.isManager) && !isAdmin;
  const visibleViews = VIEWS.filter((v) => {
    if (v.adminOnly && !isAdmin) return false;
    if (v.execOnly && !user?.canViewExec) return false;
    // Pilotage BL : Direction ou manager (même un manager ADMIN, `isManager` brut).
    if (v.execOrManager && !(user?.canViewExec || user?.isManager)) return false;
    if (v.adminOrExec && !(isAdmin || user?.canViewExec)) return false;
    if (v.adminOrExecOrReferent && !(isAdmin || user?.canViewExec || user?.isReferent)) return false;
    if (v.managerHidden && managerScoped) return false;
    return true;
  });
  // Work item #11 — la vue active est portée par l'URL (`/forecast`,
  // `/prospection`, …) pour survivre à un CTRL+R. Tant que `/auth/me` n'a pas
  // répondu, on route sur le catalogue complet : filtrer sur `visibleViews`
  // avec un `user` encore nul réécrirait `/admin` en `/forecast` avant même de
  // connaître les droits. Une fois chargés, `useViewRoute` corrige si la vue
  // demandée sort du périmètre autorisé.
  const routableViews = loading || !isAuthenticated ? VIEWS : visibleViews;
  const routableSignature = routableViews.map((v) => v.key).join(',');
  const routableKeys = useMemo(
    () => new Set(routableSignature ? routableSignature.split(',') : []),
    [routableSignature],
  );
  const { activeView, navigate } = useViewRoute(routableKeys, 'forecast');

  // Badge « comptes en attente » du menu Admin ; l'écran Utilisateurs le
  // rafraîchit à chaque chargement via `onPendingCountChange`.
  const [pendingCount, setPendingCount] = useState(0);
  const canLoadPending = isAdmin && user?.status === 'ACTIVE';
  useEffect(() => {
    if (!canLoadPending) return;
    api.admin
      .listUsers({ status: 'PENDING' })
      .then((r) => setPendingCount(r.pendingCount))
      .catch(() => {});
  }, [canLoadPending]);

  // Menu « Applications » (synotis-support#23) : la session SSO est partagée
  // entre sous-domaines, on demande à chaque autre app si le compte y a accès.
  const [appAccess, setAppAccess] = useState({});
  const userEmail = user?.email;
  useEffect(() => {
    if (!userEmail) return undefined;
    let cancelled = false;
    fetchAppAccess(ECOSYSTEM_APPS).then((a) => {
      if (!cancelled) setAppAccess(a);
    });
    return () => {
      cancelled = true;
    };
  }, [userEmail]);
  const switcherApps = visibleApps(ECOSYSTEM_APPS, { isAdmin, access: appAccess });

  const ActiveComponent =
    visibleViews.find((v) => v.key === activeView)?.Component ?? ForecastView;
  const ThemeIcon = THEME_ICONS[theme];

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Chargement…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView onLogin={login} authError={authError} />;
  }

  // Authentifié mais compte non actif (PENDING/DISABLED) : écran dédié.
  // En PENDING, useAuth poll /auth/me et bascule ici tout seul après activation.
  if (user.status !== 'ACTIVE') {
    return <AccountGateView status={user.status} email={user.email} onLogout={logout} />;
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.5rem',
          background: 'var(--nav-bg)',
          borderBottom: '1px solid var(--glass-border)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {/* Logo sur pastille blanche : le wordmark navy reste lisible en thème sombre comme clair. */}
          <span
            style={{
              display: 'inline-flex',
              background: '#fff',
              borderRadius: 10,
              padding: '6px 14px',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <img src={LogoSynosight} alt="Synosight" style={{ height: 58, width: 'auto', display: 'block' }} />
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <AppSwitcherMenu apps={switcherApps} buttonStyle={iconButtonStyle} />
          <button
            onClick={cycleTheme}
            title={THEME_LABELS[theme]}
            aria-label={THEME_LABELS[theme]}
            style={iconButtonStyle}
          >
            <ThemeIcon size={18} />
          </button>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{user?.email}</span>
          <button
            onClick={logout}
            title="Se déconnecter"
            aria-label="Se déconnecter"
            style={iconButtonStyle}
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <nav className="hg-nav-pills">
        {visibleViews.filter((v) => !v.adminOnly).map(({ key, label, Icon }) => {
          const active = activeView === key;
          return (
            <button
              key={key}
              onClick={() => navigate(key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 1rem',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--glass-border)',
                background: active ? 'var(--primary)' : 'var(--glass)',
                color: active ? '#fff' : 'var(--text)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: active ? 600 : 400,
              }}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
        {isAdmin && (
          <AdminMenu
            groups={ADMIN_GROUPS}
            activeKey={activeView}
            onNavigate={navigate}
            badges={{ 'admin-users': pendingCount }}
          />
        )}
      </nav>

      <main
        style={{
          flex: 1,
          padding: '2rem',
          width: '100%',
          // Pas de maxWidth : on veut le même comportement pleine largeur que
          // le header et la nav, pour profiter des grands écrans (tableaux
          // de forecast et de tarifs très larges).
        }}
      >
        <Suspense fallback={<div style={{ color: 'var(--text-muted)' }}>Chargement…</div>}>
          <ActiveComponent user={user} onPendingCountChange={setPendingCount} />
        </Suspense>
      </main>
    </div>
  );
}

const iconButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.4rem',
  background: 'transparent',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  color: 'var(--text-muted)',
};
